import { generateNextSpotLabels } from "@gettactic/map-builder/src/components/map/helpers/generateNextSpotLabels";
import type {
  FurnitureShape,
  IconShape,
  SpotShape
} from "@gettactic/map-builder/src/types";
import {
  createFurnitureElement,
  createIconElement,
  createNodeShape
} from "@gettactic/map-builder/src/utils/shapeFactory";
import React from "react";
import { useEditorSelector } from "../store/EditorStoreContext";
import { useDocumentMeta, useViewport } from "../store/selectors";
import type { GhostShape } from "./overlayChannel";
import { FurnitureSvg } from "./shapes/FurnitureSvg";
import { IconSvg } from "./shapes/IconSvg";
import { SpotSvg } from "./shapes/SpotSvg";

export interface GhostSvgProps {
  ghost: GhostShape;
}

/** Translucent preview of what the placement tools will drop. The spot
 *  ghost is world-sized; icon and furniture keep a constant screen size
 *  so they stay visible when zoomed out. */
export const GhostSvg: React.FC<GhostSvgProps> = ({ ghost }) => {
  const { scale } = useViewport();
  const meta = useDocumentMeta();
  const icon = useEditorSelector((state) => state.chrome.iconToolSelectedIcon);
  const furniture = useEditorSelector(
    (state) => state.chrome.furnitureToolSelectedFurniture
  );

  const shape = React.useMemo(() => {
    if (ghost.kind === "spot") {
      const label = generateNextSpotLabels(
        meta.spotLabelPattern,
        meta.lastSpotLabel ?? []
      );
      return createNodeShape({ x: 0, y: 0, text: (label ?? []).join("") });
    }
    if (ghost.kind === "icon" && icon) {
      return createIconElement({ x: 0, y: 0 }, icon);
    }
    if (ghost.kind === "furniture" && furniture) {
      return createFurnitureElement({ x: 0, y: 0 }, furniture);
    }
    return null;
  }, [ghost.kind, icon, furniture, meta.spotLabelPattern, meta.lastSpotLabel]);

  if (!shape) return null;
  const ghostScale = ghost.kind === "spot" ? 1 : 1 / scale;
  return (
    <g
      transform={`translate(${ghost.x}, ${ghost.y}) scale(${ghostScale})`}
      opacity={0.5}
    >
      {shape.type === "spot" ? <SpotSvg shape={shape as SpotShape} /> : null}
      {shape.type === "icon" ? <IconSvg shape={shape as IconShape} /> : null}
      {shape.type === "furniture" ? (
        <FurnitureSvg shape={shape as FurnitureShape} />
      ) : null}
    </g>
  );
};
